import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { ScheduleDb } from 'lib/cosmos/schedule/scheduleDb.js';
import { MoveDirection, arrayHelper } from 'lib/helpers/array-helper.class.js';
import { isNotUndefined, lastItem } from 'lib/helpers/array.utils.js';
import { cloneHelper } from 'lib/helpers/clone-helper.class.js';
import { guidHelper } from 'lib/helpers/guid-helper.class.js';
import { observableHelper } from 'lib/helpers/observable-helper.class.js';
import { WindowScrollService } from 'lib/services/window-scroll.service.js';
import { Subject, map } from 'rxjs';
import { match } from 'ts-pattern';
import { IButton } from '../buttons/index.js';
import { CoreComponent } from '../core/core.component.js';
import { DialogService, IDialogMessage, IDialogSection, SelectionDialogItem } from '../dialog/index.js';
import { IconType } from '../icons/index.js';
import { IImage } from '../ui-models.js';

export type ScheduleItemRoutes = {
	readonly main: string;
};

export type ScheduleItemRouteMap<T> = (
	itemId: string,
	selectedItem: ScheduleDb.IScheduleItem<T>,
	container: ScheduleDb.IScheduleContainer<T>
) => ScheduleItemRoutes;

export type AvailableScheduleItemGroup<T> = {
	readonly title: string;
	readonly items: ScheduleDb.IScheduleItem<T>[];
};

export type SaveSchedule<T> = {
	readonly title: string;
	readonly containers: readonly ScheduleDb.IScheduleContainer<T>[];
};

export type ExtendedScheduleContainer<T> = {
	readonly id: string;
	readonly domId: string;
	readonly items: readonly ExtendedScheduleItem<T>[];
	readonly cardButtons: readonly IButton[];
	readonly assignButtons: readonly IButton[];
	readonly title: string;
};

export type ExtendedScheduleItem<T = ScheduleDb.IScheduleContainerBaseItem> = {
	readonly title: string;
	readonly id: string;
	readonly iconType: IconType | undefined;
	readonly avatar: IImage | undefined;
	readonly data: T;
	readonly buttons: readonly IButton[];
	readonly routes: ScheduleItemRoutes;
};

type ScheduleType = 'workout' | 'diet';

type ContainerFormItem = {
	readonly title: string;
};

type ScheduleNameItem = {
	readonly name: string;
};

type ScheduleItemForm = {
	readonly notes: string | undefined;
};

type DisplayMode = 'edit' | 'preview';

@Component({
	selector: 'lib-schedule',
	templateUrl: './schedule.component.html',
	styleUrls: ['./schedule.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ScheduleComponent<T extends ScheduleDb.IScheduleContainerBaseItem> extends CoreComponent {
	public readonly scheduleType = input.required<ScheduleType>();
	public readonly routeMap = input.required<ScheduleItemRouteMap<T>>();
	public readonly scheduleItemGroups = input.required<readonly AvailableScheduleItemGroup<T>[]>();
	public readonly scheduleContainers = input.required<readonly ScheduleDb.IScheduleContainer<T>[]>();
	public readonly scheduleTitle = input.required<string | undefined>();
	public readonly customSchedules = input.required<readonly ScheduleDb.ISchedule<T>[]>();
	public readonly defaultSchedules = input.required<readonly ScheduleDb.ISchedule<T>[]>();
	public readonly enableEdit = input.required<boolean>();

	public readonly currentScheduleContainers = signal<readonly ScheduleDb.IScheduleContainer<T>[]>([]);

	public readonly currentTitle = signal<string | undefined>(undefined);
	public readonly showPreview = signal<boolean>(false);

	public readonly saveSchedule = output<SaveSchedule<T>>();
	public readonly saveScheduleAsTemplate = output<ScheduleDb.IBaseSchedule>();
	public readonly deleteSchedule = output<ScheduleDb.ISchedule<T>>();

	private readonly saveTitle = new Subject<void>();

	private readonly containersExplanation: string = `Schedules represent the way you assign workouts & diets to your clients. 
	A simple schedule can be based on days (Monday, Tuesday..), routines (Day A, Day B...), dates (16/04, 18/04) or entirely custom schedule.`;

	private readonly containerDialogMessage: IDialogMessage = {
		title: `What are containers?`,
		type: 'info',
		message: this.containersExplanation
	};

	public readonly extendedScheduleContainers = computed<
		readonly ExtendedScheduleContainer<ScheduleDb.IScheduleContainerBaseItem>[]
	>(() => {
		return this.mapToExtendedScheduleContainers(this.currentScheduleContainers());
	});

	public readonly scheduleActionButtons = computed<readonly IButton[] | undefined>(() => {
		if (this.displayMode() === 'preview') {
			return undefined;
		}

		return this.getMainScheduleButtons();
	});

	public readonly addContainerScheduleButtons = computed<readonly IButton[] | undefined>(() => {
		if (this.displayMode() === 'preview') {
			return undefined;
		}

		return [
			{
				title: 'Add container',
				buttonType: 'basic',
				buttonSize: 'default',
				click: () => {
					this.handleAddContainer();
				}
			}
		];
	});

	public readonly displayMode = computed<DisplayMode>(() => {
		if (this.showPreview()) {
			return 'preview';
		}
		if (this.enableEdit()) {
			return 'edit';
		}
		return 'preview';
	});

	public readonly scheduleItemColumnsPerRow = computed<1 | 2>(() => {
		if (this.isMobileOrTabletView()) {
			return 1;
		}

		return 2;
	});

	private firstInit = true;

	constructor(
		private readonly dialogService: DialogService,
		private readonly windowScrollService: WindowScrollService
	) {
		super();

		this.subscribeToSaveTitle();

		effect(() => {
			const scheduleContainers = this.scheduleContainers();
			this.currentScheduleContainers.set(cloneHelper.clone(scheduleContainers));
		});

		effect(() => {
			const title = this.scheduleTitle();
			this.currentTitle.set(title);
		});

		// save schedule when containers change
		effect(() => {
			const containers = this.currentScheduleContainers();
			const lastContainer = lastItem(containers);

			if (this.firstInit) {
				this.firstInit = false;
				return;
			}

			if (lastContainer) {
				this.saveCurrentSchedule();
			}
		});
	}

	handleClickButtonList(event: Event): void {
		event.preventDefault();
		event.stopPropagation();
	}

	handleTitleChanged(title: string): void {
		this.saveTitle.next();
	}

	handleShowPreviewChange(showPreview: boolean): void {
		this.showPreview.set(showPreview);
	}

	private handleAddContainer(): void {
		this.dialogService.openFormDialog<ContainerFormItem>({
			size: 'auto',
			title: 'Create new container',
			fields: [
				{
					title: 'Title',
					type: 'text',
					propertyName: 'title',
					required: true
				}
			],
			item: {
				title: ''
			},
			message: this.containerDialogMessage,
			save: (item) => {
				const newContainerId = guidHelper.shortGuid();

				this.currentScheduleContainers.update((containers) => {
					return [
						...containers,
						{
							items: [],
							id: newContainerId,
							title: item.title,
							cardButtons: this.getContainerCardButtons(newContainerId, item.title),
							assignButtons: this.getContainerAssignButtons(newContainerId)
						}
					];
				});

				// scroll to new container
				setTimeout(() => {
					this.windowScrollService.scrollToElement({
						elementId: this.getContainerDomId(newContainerId)
					});
				}, 100);
			}
		});
	}

	private getSelectDialogSection(containerId: string, group: AvailableScheduleItemGroup<T>): IDialogSection {
		return {
			id: group.title,
			name: group.title,
			dialog: (builder) => {
				return builder.selectionDialog({
					items: group.items.map<SelectionDialogItem<ScheduleDb.IScheduleItem<T>>>((scheduleItem) => {
						return {
							data: scheduleItem,
							title: scheduleItem.title,
							id: scheduleItem.id,
							iconType: scheduleItem.iconType,
							avatar: scheduleItem.avatar,
							subtitle: scheduleItem.subtitle
						};
					}),
					avatarMode: 'preferImage',
					select: (selectedItem) => {
						// item selected
						const scheduleItemId = guidHelper.shortGuid();

						// update containers
						this.currentScheduleContainers.update((containers) => {
							const containerToUpdate = containers.find((m) => m.id === containerId);

							const routeMap = this.routeMap();

							if (!routeMap) {
								throw Error(`Invalid route map`);
							}

							if (containerToUpdate) {
								// generate new id for selected item so that it is unique
								const newItemId = guidHelper.newGuid();
								const clonedSelectedItemData = cloneHelper.clone(selectedItem.data);
								clonedSelectedItemData.id = newItemId;

								const newItem: ScheduleDb.IScheduleItem<T> = {
									id: scheduleItemId,
									data: selectedItem.data,
									title: selectedItem.title,
									iconType: selectedItem.iconType,
									avatar: selectedItem.avatar
								};

								containerToUpdate.items = [...(containerToUpdate.items ?? []), newItem];
							}

							return [...containers];
						});
					},
					title: `Select ${this.scheduleType()}`,
					subtitle: `Assigned ${this.scheduleType()}s are cloned and any further changes are unique to client`
				});
			}
		};
	}

	private openSelectItemDialog(containerId: string): void {
		this.dialogService.openDialogWithSections({
			dialogId: `selectScheduleItem_${this.scheduleType()}`,
			size: 'default',
			sections: this.scheduleItemGroups().map((m) => this.getSelectDialogSection(containerId, m))
		});
	}

	private handleSelectSchedule(schedule: ScheduleDb.ISchedule<T>): void {
		const currentScheduleItems = this.currentScheduleContainers()
			.flatMap((m) => m.items)
			.filter(isNotUndefined);

		// set title
		this.currentTitle.set(schedule.system.name);

		// get containers
		const containers = this.getScheduleContainers(schedule);

		// set schedule items to first slot
		const firstSlot = containers?.[0];

		if (firstSlot) {
			firstSlot.items = currentScheduleItems;
		}

		// set containers
		this.currentScheduleContainers.set(containers);
	}

	private subscribeToSaveTitle(): void {
		super.subscribeToObservable(
			this.saveTitle.pipe(
				observableHelper.saveDebounceTime({ isMobileView: this.isMobileView() }),
				map(() => {
					this.saveCurrentSchedule();
				})
			)
		);
	}

	private openSelectScheduleDialog(): void {
		const defaultSchedules = this.defaultSchedules();
		const customSchedules = this.customSchedules();

		this.dialogService.openDialogWithSections({
			dialogId: 'openSelectScheduleDialog',
			size: 'default',
			sections: [
				{
					id: 'allSchedules',
					name: 'All schedules',
					dialog: (builder) => {
						return builder.selectionDialog<ScheduleDb.ISchedule<T>>({
							title: `Select schedule`,
							subtitle: `All assigned workouts / diets will be placed in the first available slot when changing schedule type`,
							items: [...defaultSchedules, ...customSchedules].map<
								SelectionDialogItem<ScheduleDb.ISchedule<T>>
							>((schedule) => {
								return {
									data: schedule,
									title: schedule.system.name,
									id: schedule.id
								};
							}),
							select: (schedule) => {
								this.handleSelectSchedule(schedule);
							}
						});
					}
				},
				{
					id: 'custom',
					name: 'Custom schedules',
					dialog: (builder) => {
						return builder.selectionDialog<ScheduleDb.ISchedule<T>>({
							title: `Select schedule`,
							subtitle: `All assigned workouts / diets will be placed in the first available slot when changing schedule type`,
							items: customSchedules.map((schedule) => {
								const item: SelectionDialogItem<ScheduleDb.ISchedule<T>> = {
									data: schedule,
									title: schedule.system.name,
									id: schedule.id,
									buttons: (context) => {
										const itemButtons: readonly IButton[] = [
											{
												title: 'More',
												nestedButtons: [
													{
														confirm: {
															confirmMode: 'delete',
															text: 'Are you sure to delete this schedule?'
														},
														title: 'Delete schedule',
														click: () => {
															context.closeDialog();
															this.deleteSchedule.emit(schedule);
														}
													}
												]
											}
										];

										return itemButtons;
									}
								};

								return item;
							}),
							select: (schedule) => {
								this.handleSelectSchedule(schedule);
							}
						});
					}
				}
			]
		});
	}

	private getMainScheduleButtons(): readonly IButton[] {
		const buttons: readonly IButton[] = [
			{
				title: 'Select schedule',
				buttonType: 'primary',
				buttonSize: 'default',
				click: () => {
					this.openSelectScheduleDialog();
				}
			},
			{
				title: 'More',
				buttonSize: 'default',
				nestedButtons: [
					{
						title: `Save as new template`,
						icon: 'save',
						click: () => {
							this.dialogService.openFormDialog<ScheduleNameItem>({
								size: 'auto',
								title: 'Set a name for your schedule',
								fields: [
									{
										title: undefined,
										type: 'text',
										propertyName: 'name',
										required: true
									}
								],
								item: {
									name: ''
								},
								save: (item) => {
									const newSchedule: ScheduleDb.IBaseSchedule = {
										name: item.name,
										containers: this.currentScheduleContainers().map((m) => {
											const container: ScheduleDb.IScheduleContainer = {
												id: m.id,
												title: m.title,
												items: []
											};

											return container;
										})
									};
									this.saveScheduleAsTemplate.emit(newSchedule);
								}
							});
						}
					}
				],
				click: () => {
					this.handleAddContainer();
				}
			}
		];

		return buttons;
	}

	private mapToExtendedScheduleContainers(
		containers: readonly ScheduleDb.IScheduleContainer<T>[]
	): readonly ExtendedScheduleContainer<T>[] {
		const routeMap = this.routeMap();

		if (!routeMap) {
			throw Error(`Invalid route map`);
		}

		return containers.map<ExtendedScheduleContainer<T>>((container) => {
			return {
				id: container.id,
				domId: this.getContainerDomId(container.id),
				title: container.title,
				cardButtons: this.getContainerCardButtons(container.id, container.title),
				items: (container.items ?? []).map<ExtendedScheduleItem<T>>((item) => {
					const extendedScheduleItem: ExtendedScheduleItem<T> = {
						buttons: this.getItemButtons(container, item, item.title),
						data: item.data,
						id: item.id,
						title: item.title,
						routes: routeMap(item.id, item, container),
						iconType: item.iconType,
						avatar: item.avatar
					};

					return extendedScheduleItem;
				}),
				assignButtons: this.getContainerAssignButtons(container.id)
			};
		});
	}

	private getContainerDomId(containerId: string): string {
		return `container_${containerId}`;
	}

	private saveCurrentSchedule(): void {
		this.saveSchedule.emit({
			containers: this.currentScheduleContainers(),
			title: this.currentTitle() ?? 'default'
		});
	}

	private getItemButtons(
		container: ScheduleDb.IScheduleContainer<T>,
		item: ScheduleDb.IScheduleItem<T>,
		itemTitle: string
	): IButton[] {
		return [
			{
				buttonType: 'basic',
				iconOnly: true,
				title: `Move down`,
				icon: 'arrowDown',
				hidden: () => {
					const index = (container.items ?? []).findIndex((m) => m.id === item.id);
					return !this.canMoveContainerItemDown(container.items ?? [], index);
				},
				click: () => {
					this.handleMoveContainerItem(container.id, item.id, 'down');
				}
			},
			{
				buttonType: 'basic',
				iconOnly: true,
				title: `Move up`,
				icon: 'arrowUp',
				hidden: () => {
					const index = (container.items ?? []).findIndex((m) => m.id === item.id);
					return !this.canMoveContainerItemUp(container.items ?? [], index);
				},
				click: () => {
					this.handleMoveContainerItem(container.id, item.id, 'up');
				}
			},
			{
				buttonType: 'basic',
				iconOnly: true,
				title: `Edit notes`,
				icon: 'text',
				hidden: () => {
					return false;
				},
				click: () => {
					this.openEditNotesDialog(container.id, item.id);
				}
			},
			{
				buttonType: 'basic',
				iconOnly: true,
				title: `Edit ${this.scheduleType()}`,
				icon: 'edit',
				hidden: () => {
					return false;
				},
				routerLink: this.routeMap()(item.id, item, container).main
			},
			{
				buttonType: 'basic',
				iconOnly: true,
				title: `Delete ${this.scheduleType()}`,
				icon: 'delete',
				confirm: {
					confirmMode: 'delete',
					text: `Are you sure to remove '${itemTitle}' from container?`
				},
				click: () => {
					this.currentScheduleContainers.update((containers) => {
						container.items = container?.items?.filter((m) => m.id !== item.id);

						return [...containers];
					});
				}
			}
		];
	}

	private openEditNotesDialog(containerId: string, itemId: string): void {
		const containers = this.currentScheduleContainers();
		const container = containers.find((m) => m.id === containerId);

		if (!container) {
			return;
		}

		const containerItem = container.items?.find((m) => m.id === itemId);

		if (!containerItem) {
			return;
		}

		this.dialogService.openFormDialog<ScheduleItemForm>({
			size: 'auto',
			title: 'Edit notes',
			fields: [
				{
					title: undefined,
					type: 'textarea',
					propertyName: 'notes',
					required: false
				}
			],
			item: {
				notes: containerItem.data.notes
			},
			save: (item) => {
				this.currentScheduleContainers.update((containers) => {
					const container = containers.find((m) => m.id === containerId);

					if (container) {
						const containerItem = container.items?.find((m) => m.id === itemId);

						if (containerItem) {
							containerItem.data.notes = item.notes;
						}
					}

					return [...containers];
				});
			}
		});
	}

	private getContainerAssignButtons(containerId: string): readonly IButton[] {
		if (this.displayMode() === 'preview') {
			return [];
		}

		return [
			{
				iconOnly: false,
				title: this.scheduleType() === 'workout' ? `Assign workout` : `Assign diet`,
				buttonSize: 'default',
				buttonType: 'primaryLight',
				click: () => {
					this.openSelectItemDialog(containerId);
				}
			}
		];
	}

	private getContainerCardButtons(containerId: string, title: string): readonly IButton[] {
		if (this.displayMode() === 'preview') {
			return [];
		}

		const buttons: readonly IButton[] = [
			{
				buttonType: 'basic',
				hidden: () => {
					const containers = this.currentScheduleContainers();
					const index = containers.findIndex((m) => m.id === containerId);
					return !this.canMoveContainerDown(containers, index);
				},
				iconOnly: true,
				title: `Move down`,
				icon: 'arrowDown',
				click: () => {
					const containers = this.currentScheduleContainers();
					const index = containers.findIndex((m) => m.id === containerId);
					this.handleMoveContainer(containerId, 'down');
				}
			},
			{
				buttonType: 'basic',
				hidden: () => {
					const containers = this.currentScheduleContainers();
					const index = containers.findIndex((m) => m.id === containerId);
					return !this.canMoveContainerUp(containers, index);
				},
				iconOnly: true,
				title: `Move up`,
				icon: 'arrowUp',
				click: () => {
					const containers = this.currentScheduleContainers();
					const index = containers.findIndex((m) => m.id === containerId);
					this.handleMoveContainer(containerId, 'up');
				}
			},
			{
				buttonType: 'basic',
				title: 'Edit container',
				iconOnly: true,
				icon: 'edit',
				click: () => {
					this.dialogService.openFormDialog<ContainerFormItem>({
						size: 'auto',
						title: 'Edit container',
						fields: [
							{
								title: 'Title',
								type: 'text',
								propertyName: 'title',
								required: true
							}
						],
						message: this.containerDialogMessage,
						item: {
							title: title
						},
						save: (item) => {
							this.currentScheduleContainers.update((containers) => {
								const container = containers.find((m) => m.id === containerId);

								if (container) {
									// update container title
									container.title = item.title;
								}

								return [...containers];
							});
						}
					});
				}
			},
			{
				buttonType: 'basic',
				title: 'Delete container',
				iconOnly: true,
				icon: 'delete',
				confirm: {
					confirmMode: 'delete',
					text: `Are you sure to delete this container? All items within this container will be lost.`
				},
				click: () => {
					this.currentScheduleContainers.update((containers) => {
						return containers.filter((m) => m.id !== containerId);
					});
				}
			}
		];

		return buttons;
	}

	private canMoveContainerDown(containers: readonly ScheduleDb.IScheduleContainer<T>[], index: number): boolean {
		return arrayHelper.canMoveDown(containers, index);
	}

	private canMoveContainerUp(containers: readonly ScheduleDb.IScheduleContainer<T>[], index: number): boolean {
		return arrayHelper.canMoveUp(containers, index);
	}

	private handleMoveContainer(idOfContainertoMove: string, direction: MoveDirection): void {
		// make mutable array
		const containers = Array.from(this.currentScheduleContainers());
		const index = containers.findIndex((m) => m.id === idOfContainertoMove);

		const movedContainers = [...arrayHelper.moveItem(containers, index, direction)];

		this.currentScheduleContainers.set(movedContainers);
	}

	private canMoveContainerItemDown(items: readonly ScheduleDb.IScheduleItem<T>[], index: number): boolean {
		return arrayHelper.canMoveDown(items, index);
	}

	private canMoveContainerItemUp(items: readonly ScheduleDb.IScheduleItem<T>[], index: number): boolean {
		return arrayHelper.canMoveUp(items, index);
	}

	private handleMoveContainerItem(idOfContainer: string, idOfItemToMove: string, direction: MoveDirection): void {
		this.currentScheduleContainers.update((containers) => {
			const container = containers.find((m) => m.id === idOfContainer);

			if (!container) {
				return containers;
			}

			if (container.items) {
				const index = container.items.findIndex((m) => m.id === idOfItemToMove);
				const movedItems = arrayHelper.moveItem(Array.from(container.items), index, direction);

				container.items = movedItems;
			}

			return [...containers];
		});
	}

	private getScheduleContainers(schedule: ScheduleDb.ISchedule<T>): readonly ScheduleDb.IScheduleContainer<T>[] {
		if (!schedule.specialType) {
			return schedule.containers;
		}

		return match(schedule.specialType)
			.returnType<readonly ScheduleDb.IScheduleContainer<T>[]>()
			.with('UpcomingWeeks', () => this.getUpcomingWeeksContainers(schedule.upcomingWeeks ?? 0))
			.exhaustive();
	}

	private getUpcomingWeeksContainers(weeks: number): readonly ScheduleDb.IScheduleContainer<T>[] {
		const scheduleContainers: ScheduleDb.IScheduleContainer<T>[] = [];

		const startDate = this.timeService.now();
		const endDate = startDate.plus({ weeks: weeks });
		let currentDate = startDate;

		while (currentDate.toJSDate() < endDate.toJSDate()) {
			scheduleContainers.push({
				id: `${currentDate.year}_${currentDate.month}_${currentDate.day}`,
				title: `${this.timeService.formatDateWithoutYear(currentDate)}`
			});

			currentDate = currentDate.plus({ day: 1 });
		}

		return scheduleContainers;
	}
}
